import React, { useRef } from 'react';
import { ITagsField, Tag } from './interfaces';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { getUniqueSelectedTagsValue, isNewTag } from './helper';

export const TagsField: React.FC<ITagsField> = ({
  label,
  placeholder,
  existingTags,
  value,
  error,
  sx,
  maxTags,
  required,
  onChange,
  size = 'small',
  createTags = false,
  disabled = false,
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const currentValue = (() => {
    const selectedExistingTags = existingTags?.filter((option) =>
      value?.includes(option.id)
    );
    const selectedNewTags = value.filter((option) => isNewTag(option));

    return [...selectedNewTags, ...(selectedExistingTags || [])];
  })();
  const isMaxReached = ((): boolean => {
    if (!maxTags) {
      return false;
    }

    return currentValue.length >= maxTags;
  })();

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target;
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      bubbles: true,
      cancelable: true,
    });

    input.dispatchEvent(enterEvent);
  };

  return (
    <Autocomplete
      multiple
      size={size}
      disabled={disabled}
      freeSolo={createTags}
      options={existingTags || []}
      getOptionLabel={(option) => {
        if (isNewTag(option)) {
          return option as string;
        }

        return (option as Tag).name;
      }}
      defaultValue={[]}
      filterSelectedOptions
      value={currentValue}
      onChange={(event, selectedOptions) => {
        const options = getUniqueSelectedTagsValue(
          selectedOptions,
          existingTags || []
        );

        onChange(options.slice(0, maxTags));
        inputRef.current?.blur();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      }}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error}
          label={label}
          required={required}
          onBlur={handleBlur}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            disabled: disabled || isMaxReached,
          }}
          inputRef={inputRef}
        />
      )}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });

          return (
            <Chip
              key={key}
              {...tagProps}
              label={
                isNewTag(option) ? (option as string) : (option as Tag).name
              }
              color="default"
              variant={isNewTag(option) ? 'outlined' : 'filled'}
            />
          );
        })
      }
    />
  );
};
